"use client";

import { useId } from "react";
import styles from "./Toggle.module.css";

/**
 * iOS-style toggle switch. Use for boolean state where the meaning is
 * "on/off" (e.g. "Ignore already-imported videos", feature flags). For
 * multi-selection cases (where a user is picking N items from a list),
 * prefer the Checkbox component.
 *
 * Props:
 *   - checked: boolean
 *   - onChange: (nextChecked: boolean) => void
 *   - label?: ReactNode — clickable text rendered to the LEFT of the
 *     switch. Either pass this, or wrap the Toggle yourself with the
 *     label you want.
 *   - disabled?: boolean
 *   - id?: string — for explicit <label htmlFor> linking
 *   - "aria-label"?: string — required when no `label` prop is set
 */
export default function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
  id,
  className,
  "aria-label": ariaLabel,
}) {
  const autoId = useId();
  const inputId = id ?? `toggle-${autoId}`;

  const trackEl = (
    <span className={styles.track}>
      <input
        id={inputId}
        type="checkbox"
        className={styles.input}
        checked={!!checked}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked)}
        aria-label={label ? undefined : ariaLabel}
      />
      <span className={styles.slider} aria-hidden="true" />
    </span>
  );

  if (!label) return trackEl;

  return (
    <label
      htmlFor={inputId}
      className={[
        styles.row,
        disabled ? styles.disabled : "",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className={styles.label}>{label}</span>
      {trackEl}
    </label>
  );
}
